import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react'
import { MainButton } from '../../../Button/MainButton'
import { Dialog, DialogTitle, DialogContent, makeStyles, ButtonBase } from '@material-ui/core'
import { FONT_WEIGHTS } from '../../../../utils'
import DaycareWizardContext from '../DaycareWizardContext'
import useDaycareVisit from '../../../../hooks/useDaycareVisit'
import PetAvatar from '../../../Common/PetAvatar'
import AddSubtractItemCount from '../../../Common/Buttons/AddSubtractItemCount'
import { dangers, gray } from '../../../../utils/colors'
import { showError } from '../../../../actions/misc/misc.actions'
import DangerConfirmationDialog from '../../../Common/Dialogs/DangerConfirmation'

const useStyles = makeStyles(() => ({
	content: { display: 'flex', flexDirection: 'column', gap: '3rem' },
	header: {
		marginBottom: '.8rem'
	},
	titleText: {
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		textTransform: 'uppercase'
	},
	petsContainer: {
		display: 'flex',
		flexDirection: 'column'
	},
	petContainer: {
		display: 'flex',
		flex: 1
	},
	actionsContainer: {
		justifyContent: 'flex-start',
		marginTop: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '2rem',
		marginTop: '4rem'
	},
	deleteButton: {
		alignSelf: 'flex-start',
		color: dangers[500],
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		letterSpacing: '0.078125rem',
		textTransform: 'uppercase',
		padding: '.5rem 0 !important'
	},
	mainActions: {
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'flex-start',
		gap: '1rem',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		}
	},
	divider: {
		height: '0.0625rem',
		backgroundColor: gray[500],
		margin: '1.5rem 0'
	}
}))

const EditAddOnsDialog = ({
	open,
	onSave,
	onCancel,
	onDelete,
	title = 'Enrichments',
	addon_id = '',
	addon_choice_id = ''
}) => {
	const classes = useStyles()
	const {
		wizardContextProvider: { wizard_state }
	} = useContext(DaycareWizardContext)

	const [editableAddons, setEditableAddons] = useState([])
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
	const { facility, updating } = useDaycareVisit()

	useEffect(() => {
		const { add_on_services } = wizard_state
		setEditableAddons([...add_on_services])
	}, [wizard_state])

	const description = useMemo(() => {
		return `Please choose the number of ${title} you would like to allocate for each pet listed on this appointment.`
	}, [title])

	const facilityAddons = useMemo(() => {
		if (!facility || !facility.addOnTypes) {
			return []
		}
		return facility.addOnTypes
	}, [facility])

	const addOnOptions = useMemo(() => {
		const { pets, add_on_services } = wizard_state
		const petsAddOnsMap = new Map()
		const nonMealAddons = add_on_services.filter((addon) => addon.category !== 'meal')

		const options = nonMealAddons.filter((addon) => {
			if (addon.addon_id === addon_id) {
				if (addon_choice_id > '' && addon.addon_choice_id !== addon_choice_id) {
					return false
				}
				return true
			}
			return false
		})

		for (const pet of pets) {
			const { _id } = pet
			if (!petsAddOnsMap.has(_id)) {
				petsAddOnsMap.set(_id, { petDetails: pet, addons: [] })
			}
		}

		options.forEach((option) => {
			const { pet_id } = option
			if (petsAddOnsMap.has(pet_id)) {
				const existing = petsAddOnsMap.get(pet_id)
				petsAddOnsMap.set(pet_id, { ...existing, addons: [...existing.addons, option] })
			}
		})

		return [...petsAddOnsMap.values()]
	}, [addon_id, addon_choice_id, wizard_state])

	const handleCofirmDeleteClose = useCallback(() => {
		setConfirmDeleteOpen(false)
	}, [setConfirmDeleteOpen])

	const handleCofirmDelete = useCallback(() => {
		setConfirmDeleteOpen(true)
	}, [setConfirmDeleteOpen])

	const handleOnDelete = useCallback(() => {
		handleCofirmDeleteClose()
		if (onDelete) {
			onDelete()
		}
	}, [onDelete, handleCofirmDeleteClose])

	const handleOnSave = useCallback(() => {
		if (onSave) {
			onSave(editableAddons)
		}
	}, [onSave, editableAddons])

	const handleOnCancel = useCallback(() => {
		if (onCancel) {
			onCancel()
		}
	}, [onCancel])

	const handleEditAddons = useCallback(
		({ pet_id, addons, count }) => {
			const { _id: visitId } = wizard_state

			let baseAddon = null

			// If the array is empty then this addon was not assigned to this pet in the addons step of the wizard
			if (addons.length === 0) {
				baseAddon = facilityAddons.find((addon) => addon._id === addon_id)
			} else {
				baseAddon = addons[0]
			}

			if (!baseAddon) {
				showError(
					'There was an error adding that enrichment for this pet. Number of enrichments not updated.'
				)
			}

			const {
				_id,
				addon_id: baseAddonId,
				addon_choice_id: baseAddonChoiceId,
				category,
				facility_id,
				name,
				time,
				type
			} = baseAddon

			setEditableAddons((prevAddons) => {
				// remove all of the specific addon for that pet initially
				const newAddons = prevAddons.filter((addon) => {
					if (addon.type === 'meal') {
						return true
					}
					if (addon.pet_id !== pet_id) {
						return true
					}
					if (addon.addon_id !== baseAddonId) {
						return true
					}
					return false
				})

				if (count === 0) {
					return newAddons
				}

				const cleanAddon = {
					addon_id: baseAddonId || _id,
					category,
					facility_id,
					name,
					time: time || 'midday',
					type,
					pet_id,
					visit_id: visitId
				}

				if (baseAddonChoiceId > '' || addon_choice_id > '') {
					cleanAddon.addon_choice_id = baseAddonChoiceId || addon_choice_id
				}

				for (let i = 0; i < count; i++) {
					newAddons.push(cleanAddon)
				}

				return newAddons
			})
		},
		[setEditableAddons, facilityAddons, addon_id, wizard_state]
	)

	const handleOnAdd = useCallback(
		({ pet_id, addons, count }) => {
			handleEditAddons({ pet_id, addons, count })
		},
		[handleEditAddons]
	)

	const handleOnSubtract = useCallback(
		({ pet_id, addons, count }) => {
			handleEditAddons({ pet_id, addons, count })
		},
		[handleEditAddons]
	)

	return (
		<>
			<DangerConfirmationDialog
				title="Delete Enrichment"
				description="Are you sure you want to remove this enrichment from the invoice? This will remove it for all of the pets listed on this appointment."
				open={confirmDeleteOpen}
				onConfirmDangerousAction={handleOnDelete}
				onCancel={handleCofirmDeleteClose}
				dangerButtonText="Delete"
			/>
			<Dialog
				PaperProps={{
					style: {
						width: 450,
						maxWidth: 450,
						minWidth: 450
					}
				}}
				maxWidth="md"
				open={open}
				onClose={() => null}>
				<DialogTitle>
					<div className={classes.header}>
						<div className={classes.titleText}>{`Edit ${title}`}</div>
					</div>
				</DialogTitle>
				<DialogContent className={classes.content}>
					<div>{description}</div>
					<div className={classes.petsContainer}>
						{addOnOptions.length > 0 ? (
							addOnOptions.map(({ petDetails, addons }, i) => {
								return (
									<>
										<div key={petDetails._id} className={classes.petContainer}>
											<PetAvatar petName={petDetails.name} imageUrl={petDetails.image_url} />
											<div>
												<AddSubtractItemCount
													disabled={updating}
													count={addons.length}
													onAdd={(count) =>
														handleOnAdd({ pet_id: petDetails._id, addons, count })
													}
													onSubtract={(count) =>
														handleOnSubtract({ pet_id: petDetails._id, addons, count })
													}
												/>
											</div>
										</div>
										{i < addOnOptions.length - 1 && <div className={classes.divider} />}
									</>
								)
							})
						) : (
							<div>no enrichments</div>
						)}
					</div>
				</DialogContent>
				<div className={classes.actionsContainer}>
					<ButtonBase
						className={classes.deleteButton}
						disableRipple
						onClick={handleCofirmDelete}
						disabled={updating}>
						Delete Enrichment
					</ButtonBase>
					<div className={classes.mainActions}>
						<MainButton
							style={{ paddingTop: '6px' }}
							variant="contained"
							label="Save"
							onClick={handleOnSave}
							disabled={updating}
						/>
						<MainButton label="Cancel" onClick={handleOnCancel} disabled={updating} />
					</div>
				</div>
			</Dialog>
		</>
	)
}

export default EditAddOnsDialog
